interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  count?: number;
}

function SkeletonLine({
  className = '',
  variant = 'text',
  width,
  height,
}: Omit<SkeletonProps, 'count'>) {
  const baseClasses = 'animate-pulse rounded';
  const variantClasses = {
    text: 'h-4 rounded-md',
    circular: 'rounded-full',
    rectangular: 'rounded-xl',
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={{
        width: width ?? '100%',
        height: height ?? (variant === 'text' ? undefined : '100%'),
        background: 'var(--border-subtle)',
      }}
    />
  );
}

export default function Skeleton({
  count = 1,
  ...props
}: SkeletonProps) {
  if (count <= 1) return <SkeletonLine {...props} />;
  return (
    <div className="flex flex-col gap-2.5">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonLine key={i} {...props} />
      ))}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div
      className="rounded-2xl p-5 border"
      style={{
        background: 'var(--bg-elevated)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <Skeleton variant="circular" width={36} height={36} />
        <div className="flex-1">
          <Skeleton variant="text" width="60%" height={16} />
          <Skeleton variant="text" width="35%" height={12} className="mt-2" />
        </div>
      </div>
      <Skeleton variant="text" count={3} />
    </div>
  );
}

export function SkeletonCardGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="card-grid">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export function TabLoadingSkeleton() {
  return (
    <div className="space-y-5 p-2">
      <div className="flex items-center justify-between">
        <Skeleton variant="text" width="40%" height={28} />
        <Skeleton variant="rectangular" width={120} height={36} className="rounded-xl" />
      </div>
      <SkeletonCardGrid count={6} />
    </div>
  );
}
